import React, { useState } from 'react';
import classNames from 'classnames';

import { IconButton, Layout, ProductImg, Text } from 'ds';
import { formatUsd } from 'helpers';

import AIAssistantConsumablesPickerConfirmedAdminBudget from './AIAssistantConsumablesPickerConfirmedAdminBudget';
import type {
  AIAssistantConsumablesPickerConfirmedProps,
  ConsumablesPickerConfirmedApplyMode
} from './AIAssistantConsumablesPickerConfirmedTypes';

export type {
  AIAssistantConsumablesPickerConfirmedProps,
  ConsumablesPickerConfirmedApplyMode,
  ConsumablesPickerConfirmedLineItem
} from './AIAssistantConsumablesPickerConfirmedTypes';

function itemCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'item' : 'items'}`;
}

function confirmationTitle(applyMode: ConsumablesPickerConfirmedApplyMode): string {
  return applyMode === 'recurring' ? 'Added to your subscription' : 'Added to your next order';
}

function collapsedSummaryPhrase(applyMode: ConsumablesPickerConfirmedApplyMode): string {
  return applyMode === 'recurring' ? 'added to subscription' : 'added to next order';
}

const HeaderCheckBadge: React.FC = () => (
  <div className="AIAssistantConsumablesPickerConfirmed-checkBadge" aria-hidden>
    <svg width={10} height={8} viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M1 4L3.5 6.5L9 1" stroke="#ffffff" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </div>
);

const AIAssistantConsumablesPickerConfirmed: React.FC<AIAssistantConsumablesPickerConfirmedProps> = ({
  applyMode,
  lineItems,
  totalCents,
  budgetCheckOrderId = null
}) => {
  const [expanded, setExpanded] = useState(true);
  const itemCount = lineItems.length;
  const countLabel = itemCountLabel(itemCount);
  const title = confirmationTitle(applyMode);
  const summaryTail = collapsedSummaryPhrase(applyMode);

  const chevronWrapClass = classNames('AIAssistantConsumablesPickerConfirmed-chevronWrap', {
    'AIAssistantConsumablesPickerConfirmed-chevronWrap--collapsed': expanded === false
  });

  const toggleExpanded = () => {
    setExpanded(v => !v);
  };

  return (
    <Layout
      __className={classNames(
        'AIAssistantConsumablesPickerConfirmed',
        expanded
          ? 'AIAssistantConsumablesPickerConfirmed--expanded'
          : 'AIAssistantConsumablesPickerConfirmed--collapsed'
      )}
      direction="column"
      width="100%"
      minWidth={0}
    >
      <div className="AIAssistantConsumablesPickerConfirmed-expandedHeader">
        <Layout
          __className="AIAssistantConsumablesPickerConfirmed-header"
          direction="row"
          align="center"
          justify="space-between"
          gap={8}
          width="100%"
          minWidth={0}
        >
          <Layout direction="row" align="center" gap={8} flexGrow={1} minWidth={0}>
            <HeaderCheckBadge />
            <Layout direction="column" gap={2} flexGrow={1} minWidth={0}>
              <span className="AIAssistantConsumablesPickerConfirmed-title">
                <Text size="body-sm" color="gray-900">
                  {title}
                </Text>
              </span>
            </Layout>
          </Layout>
          <Layout direction="row" align="center" gap={8} flexShrink={0}>
            <span className="AIAssistantConsumablesPickerConfirmed-itemCount">
              <Text size="body-xs" color="gray-600" wrap={false}>
                {countLabel}
              </Text>
            </span>
            <span className={chevronWrapClass}>
              <IconButton
                type="noBackground"
                name="downChevron"
                size="sm"
                iconColor="gray-600"
                stroke={2}
                context="consumables_confirmation_expand_toggle"
                onClick={toggleExpanded}
              />
            </span>
          </Layout>
        </Layout>
      </div>
      <div className="AIAssistantConsumablesPickerConfirmed-collapsedBar">
        <Layout direction="row" align="center" justify="space-between" gap={8} width="100%" minWidth={0} wrap>
          <Layout direction="row" align="center" gap={8} flexGrow={1} minWidth={0}>
            <HeaderCheckBadge />
            <Text size="body-sm" semibold>
              {itemCount} items {summaryTail}
            </Text>
          </Layout>
          <Layout direction="row" align="center" gap={8} flexShrink={0}>
            <Text size="body-sm" color="gray-900" semibold wrap={false}>
              {formatUsd(totalCents / 100)}
            </Text>
            <span className={chevronWrapClass}>
              <IconButton
                type="noBackground"
                name="downChevron"
                size="sm"
                iconColor="gray-600"
                stroke={2}
                context="consumables_confirmation_expand_toggle"
                onClick={toggleExpanded}
              />
            </span>
          </Layout>
        </Layout>
      </div>
      <div className="AIAssistantConsumablesPickerConfirmed-body">
        <div className="AIAssistantConsumablesPickerConfirmed-bodyInner">
          <Layout direction="column" width="100%" minWidth={0}>
            {lineItems.map((item, index) => (
              <Layout
                key={item.productId}
                __className={classNames('AIAssistantConsumablesPickerConfirmed-lineItem', {
                  'AIAssistantConsumablesPickerConfirmed-lineItem--last': index === lineItems.length - 1
                })}
                direction="row"
                align="center"
                gap={12}
                width="100%"
                minWidth={0}
              >
                <Layout __className="AIAssistantConsumablesPickerConfirmed-thumbWrap" flexShrink={0}>
                  <ProductImg
                    srcKey={item.imageKey}
                    size="mini"
                    alt={item.title}
                    style={{ width: 32, height: 32, borderRadius: 4 }}
                  />
                </Layout>
                <Layout direction="column" gap={2} flexGrow={1} minWidth={0}>
                  <span className="AIAssistantConsumablesPickerConfirmed-lineTitle">
                    <Text size="body-xs" color="gray-900" maxLines={2}>
                      {item.title}
                    </Text>
                  </span>
                  <span className="AIAssistantConsumablesPickerConfirmed-lineQty">
                    <Text size="body-xs" color="gray-600">
                      qty {item.quantity}
                    </Text>
                  </span>
                </Layout>
                <span className="AIAssistantConsumablesPickerConfirmed-linePrice">
                  <Text size="body-xs" color="gray-700" wrap={false}>
                    {formatUsd(item.lineTotalCents / 100)}
                  </Text>
                </span>
              </Layout>
            ))}
          </Layout>
          <Layout
            __className="AIAssistantConsumablesPickerConfirmed-totalRow"
            direction="row"
            justify="space-between"
            align="center"
            width="100%"
            minWidth={0}
          >
            <span className="AIAssistantConsumablesPickerConfirmed-totalLabel">
              <Text size="body-sm" color="gray-900">
                Total added
              </Text>
            </span>
            <Text size="body-sm" color="gray-900" semibold wrap={false}>
              {formatUsd(totalCents / 100)}
            </Text>
          </Layout>
        </div>
      </div>
      <AIAssistantConsumablesPickerConfirmedAdminBudget orderId={budgetCheckOrderId} applyMode={applyMode} />
    </Layout>
  );
};

export default AIAssistantConsumablesPickerConfirmed;
